/**
 * ログイン画面
 */
import { css } from "@emotion/react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Button, InputAdornment } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { FirebaseError } from "firebase/app";
import { NextPage } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  ChangeEvent,
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";

import {
  AccountForm,
  AccountFormTextField,
  accountFormLinkStyle,
} from "~/components/account/AccountForm";
import PageHead from "~/components/common/PageHead";
import { loginWithFirebaseAuth } from "~/lib/client/auth";
import { getFirebaseAuth } from "~/lib/client/firebase";
import { useSignupExamContext } from "~/lib/client/hooks/useSignupExamContext";
import { Layout } from "~/lib/client/layout";
import {
  authDestination,
  buildAccountHref,
  safeInternalRedirect,
} from "~/lib/client/signupExamContext";
import { constants } from "~/lib/constants";

const LoginPage: NextPage = () => {
  const router = useRouter();
  const examContextState = useSignupExamContext(
    router.query.exam_slug,
    router.isReady
  );
  const targetPath = authDestination(
    examContextState.context,
    router.query.redirect,
    "/me"
  );
  const signupHref = useMemo(
    () =>
      buildAccountHref("/account/signup", {
        examSlug:
          examContextState.context?.examSlug ?? examContextState.examSlug,
        redirect: safeInternalRedirect(router.query.redirect),
      }),
    [
      examContextState.context?.examSlug,
      examContextState.examSlug,
      router.query.redirect,
    ]
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loginOpInProgress, setLoginOpInProgress] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [firebaseUser, loading] = useAuthState(getFirebaseAuth());
  const hasRedirected = useRef(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleSubmitOnClick = useCallback(async () => {
    setLoginOpInProgress(true);
    setErrorMessage("");
    try {
      await loginWithFirebaseAuth(email, password);
      await router.push(targetPath);
    } catch (e) {
      if (e instanceof FirebaseError) {
        switch (e.code) {
          case "auth/email-already-exists":
            setErrorMessage("そのメールアドレスはすでに利用されています。");
            break;
          case "auth/invalid-email":
            setErrorMessage("登録されたメールアドレスは無効です。");
            break;
          case "auth/wrong-password":
            setErrorMessage("パスワードが間違えています");
            break;
          case "auth/too-many-requests":
            setErrorMessage(
              "ログインの失敗回数が上限に達しました。ログインするにはしばらくお待ち下さい。"
            );
            break;
          case "auth/user-not-found":
            setErrorMessage("そのユーザは見つかりませんでした。");
            break;
          default:
            setErrorMessage(
              `${e.code} 次のリンクより管理者にご連絡ください。 ${constants.contactForm.url}`
            );
            break;
        }
      }
    }
    setLoginOpInProgress(false);
  }, [email, password, router, targetPath]);

  useEffect(() => {
    if (
      !loading &&
      !examContextState.isLoading &&
      firebaseUser &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      router.push(targetPath);
    }
  }, [loading, firebaseUser, examContextState.isLoading, targetPath, router]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      await handleSubmitOnClick();
    },
    [handleSubmitOnClick]
  );

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setEmail(e.currentTarget.value);
  };

  const handleChangePassword = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setPassword(e.currentTarget.value);
  };

  const handleMouseDownPassword = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  if (!router.isReady || loading || examContextState.isLoading) {
    return (
      <>
        <PageHead />
      </>
    );
  }

  return (
    <>
      <PageHead />
      <Layout layoutType={"YTLayout"}>
        <main css={rootStyle}>
          <AccountForm
            title={<h2>ログイン</h2>}
            secondaryText={
              <p>
                または
                <Link css={accountFormLinkStyle} href={signupHref}>
                  無料でアカウントを作成する
                </Link>
              </p>
            }
            onSubmit={handleSubmit}
            error={{ errorMessage, setErrorMessage }}
          >
            <AccountFormTextField
              id="input-email"
              label="Email"
              type="email"
              required
              onChange={handleChangeEmail}
            />

            <AccountFormTextField
              id="input-password"
              label="Password"
              onChange={handleChangePassword}
              type={showPassword ? "text" : "password"}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      sx={{ color: "#282828" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              disabled={loginOpInProgress}
              variant="contained"
              sx={{
                marginTop: "1.25rem",
              }}
              type={"submit"}
            >
              {loginOpInProgress ? "ログイン中..." : "ログイン"}
            </Button>
            <Link href={"/account/password-reset"} css={linkContainerStyle}>
              <p css={accountFormLinkStyle}>パスワードを忘れた方はこちら</p>
            </Link>
          </AccountForm>
        </main>
      </Layout>
    </>
  );
};

const rootStyle = css`
  display: grid;
  place-items: center;
  height: 90vh;
  width: 100%;
`;

const linkContainerStyle = css`
  margin: 0.5rem;
`;

export default LoginPage;
